import { useEffect } from 'react';
import type { SettingsState } from '../types';

interface CreateGameDialogProps {
  settings: SettingsState;
  busy: boolean;
  error: string;
  onChange: (settings: SettingsState) => void;
  onConfirm: () => void;
  onClose: () => void;
}

const DESCRIPTIONS = {
  competitive:
    'Race for points. Correct answers score by difficulty and speed.',
  cooperative:
    'No points. Everyone works from the same shared board.',
  freeForAll:
    'Any player can submit any word at any time.',
  turnBased:
    'Players take turns submitting one word each.',
  easy:
    'Press Enter after a word to check it immediately.',
  advanced:
    'Fill the board privately, then use I’m Done to check everything at once.',
};

interface SegmentedControlProps {
  label: string;
  description: string;
  options: Array<{ label: string; selected: boolean; onSelect: () => void }>;
}

function SegmentedControl({ label, description, options }: SegmentedControlProps) {
  return (
    <div className="option-group">
      <span className="option-group-title">{label}</span>
      <div className="mode-slider" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            aria-pressed={option.selected}
            className={option.selected ? 'selected' : ''}
            key={option.label}
            onClick={option.onSelect}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>
      <small>{description}</small>
    </div>
  );
}

export function CreateGameDialog({
  settings,
  busy,
  error,
  onChange,
  onConfirm,
  onClose,
}: CreateGameDialogProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        aria-label="New game settings"
        aria-modal="true"
        className="modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <h2>New game</h2>
        <p className="modal-hint">
          Pick how this game plays. Settings are locked once the game is created.
        </p>
        <SegmentedControl
          description={settings.mode === 'competitive' ? DESCRIPTIONS.competitive : DESCRIPTIONS.cooperative}
          label="Mode"
          options={[
            {
              label: 'Competitive',
              selected: settings.mode === 'competitive',
              onSelect: () => onChange({ ...settings, mode: 'competitive' }),
            },
            {
              label: 'Cooperative',
              selected: settings.mode === 'cooperative',
              onSelect: () => onChange({ ...settings, mode: 'cooperative' }),
            },
          ]}
        />
        <SegmentedControl
          description={settings.playType === 'free-for-all' ? DESCRIPTIONS.freeForAll : DESCRIPTIONS.turnBased}
          label="Play type"
          options={[
            {
              label: 'Free-for-all',
              selected: settings.playType === 'free-for-all',
              onSelect: () => onChange({ ...settings, playType: 'free-for-all' }),
            },
            {
              label: 'Turn-based',
              selected: settings.playType === 'turn-based',
              onSelect: () => onChange({ ...settings, playType: 'turn-based' }),
            },
          ]}
        />
        <SegmentedControl
          description={settings.answerCheckMode === 'easy' ? DESCRIPTIONS.easy : DESCRIPTIONS.advanced}
          label="Answer check"
          options={[
            {
              label: 'Easy',
              selected: settings.answerCheckMode === 'easy',
              onSelect: () => onChange({ ...settings, answerCheckMode: 'easy' }),
            },
            {
              label: 'Advanced',
              selected: settings.answerCheckMode === 'advanced',
              onSelect: () => onChange({ ...settings, answerCheckMode: 'advanced' }),
            },
          ]}
        />
        <label>
          <span>Turn limit</span>
          <select
            disabled={settings.playType === 'free-for-all'}
            onChange={(event) => onChange({ ...settings, turnLimit: Number(event.target.value) })}
            value={String(settings.turnLimit ?? 120)}
          >
            <option value="60">60s</option>
            <option value="120">120s</option>
            <option value="300">300s</option>
          </select>
        </label>
        {error && <p className="notice error">{error}</p>}
        <div className="modal-actions">
          <button className="ghost-button" onClick={onClose} type="button">
            Cancel
          </button>
          <button disabled={busy} onClick={onConfirm} type="button">
            {busy ? 'Creating…' : 'Create game'}
          </button>
        </div>
      </div>
    </div>
  );
}
